import React, { useEffect, useRef, useState } from 'react';
import { MapPinIcon } from '@heroicons/react/24/solid';
import api from '../services/api';
import ChassisLocationMapModal from './ChassisLocationMapModal';
import {
  chassisTrackingTitle,
  type ChassisTrackingStatus,
} from '../utils/chassisTracking';

export interface ChassisLocationButtonProps {
  /** Chassis unit number to look up (matches bb_radar_chassis.identifier). */
  unit: string;
  /**
   * Tracking status from `getChassisTrackingStatus` — green pin for 'tracked',
   * red for either failure reason (tooltip says which). Defaults to 'unknown',
   * which renders the plain green pin so callers without the equipment list
   * behave as before.
   */
  status?: ChassisTrackingStatus;
  className?: string;
}

/**
 * GPS icon that fetches a single chassis's latest reported location from
 * `/bb-radar/chassis/{identifier}` and shows it on a map. If the chassis has
 * no tracking data, a brief inline message is shown instead.
 *
 * The pin is colored ahead of the click: green when the unit is an active GWI
 * equipment-list chassis that BB Radar has coordinates for, red otherwise. Red
 * stays clickable — the lookup is still attempted and flashes its message.
 */
const ChassisLocationButton: React.FC<ChassisLocationButtonProps> = ({
  unit,
  status = 'unknown',
  className,
}) => {
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const messageTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (messageTimer.current) clearTimeout(messageTimer.current);
    };
  }, []);

  const flashMessage = (msg: string) => {
    setMessage(msg);
    if (messageTimer.current) clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => setMessage(null), 3000);
  };

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const trimmed = unit?.trim();
    if (!trimmed || loading) return;
    setLoading(true);
    setMessage(null);
    try {
      const resp = await api.get(`/bb-radar/chassis/${encodeURIComponent(trimmed)}`);
      const lat = resp.data?.latitude;
      const lng = resp.data?.longitude;
      if (lat == null || lng == null) {
        flashMessage('No location data for this chassis');
      } else {
        setLocation({ lat: Number(lat), lng: Number(lng) });
      }
    } catch {
      flashMessage('No location data for this chassis');
    } finally {
      setLoading(false);
    }
  };

  const disabled = !unit?.trim() || loading;
  const untracked = status === 'not-tracked' || status === 'not-in-equipment-list';
  const colorClass = disabled
    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
    : untracked
      ? 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 cursor-pointer'
      : 'text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 cursor-pointer';

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className={`p-0.5 transition-colors ${colorClass} ${className ?? ''}`}
        title={chassisTrackingTitle(status)}
        aria-label="Show Chassis Location"
      >
        <MapPinIcon className="w-4 h-4" />
      </button>
      {message && (
        <span
          className="absolute top-full right-0 mt-1 z-50 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-[0.65625rem] text-white shadow-lg dark:bg-gray-700"
          role="status"
        >
          {message}
        </span>
      )}
      {location && (
        <ChassisLocationMapModal
          unit={unit}
          lat={location.lat}
          lng={location.lng}
          onClose={() => setLocation(null)}
        />
      )}
    </span>
  );
};

export default ChassisLocationButton;
